import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import {
  GenericMediaInfo,
  MediaListView,
} from '@euler/app/components/media/MediaListView';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { ErrorText } from '@euler/components/form/Input';
import { MaybeText } from '@euler/components/MaybeText';
import { FontFamily } from '@euler/components/typography';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaService } from '@euler/lib/media/MediaService';
import { ExtendedPropertyAssetInfo, ExtendedPropertyDef } from '@euler/model';
import { isNotNull, mimeTypeFromFileUri, withConfirmation } from '@euler/utils';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import produce from 'immer';
import { nanoid } from 'nanoid/non-secure';
import { memo, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { useTheme } from 'styled-components';
import invariant from 'tiny-invariant';

export const ExtendedPropertyAssetsUpload = memo(
  ({
    width,
    readonly,
    property,
    defaultValue,
    helpText,
    onChange,
  }: {
    width: number;
    property: ExtendedPropertyDef;
    readonly?: boolean;
    helpText?: string;
    defaultValue?: ExtendedPropertyAssetInfo[];
    onChange?: (assets: ExtendedPropertyAssetInfo[]) => void;
  }) => {
    invariant(property.type === 'assets');

    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const { fileUploader } = useOrderFlowContext();
    const navigation = useAppNavigation();

    const defaultValueRef = useRef(defaultValue ?? []);

    const [error, setError] = useState<string | undefined>();

    const medias$ = useCreation(
      () =>
        new BehaviorSubject<GenericMediaInfo[]>(
          defaultValueRef.current.map(x => ({
            id: x.id,
            key: x.id,
            url: x.url,
            type: x.type,
            status: 'uploaded',
            source: {
              type: 'remote',
              url: x.url,
              cid: x.id,
            },
          })),
        ),
      [],
    );

    const [medias] = useBehaviorSubject(medias$);

    const limit = property.limit ?? 10;
    const max = Math.max(0, limit - medias.length);

    const interacted = useRef(false);

    const updateError = usePersistFn((count: number) => {
      setError(property.required && !count ? '请至少上传一个文件' : undefined);
    });

    const onMediasUpdated = usePersistFn((updated: GenericMediaInfo[]) => {
      onChange?.(
        updated
          .map<ExtendedPropertyAssetInfo | undefined>(x =>
            x.source?.type === 'remote'
              ? {
                  id: String(x.id!),
                  type: x.type === 'image' ? 'image' : 'video',
                  url: x.source.url,
                  title: x.title,
                  description: x.description,
                }
              : undefined,
          )
          .filter(isNotNull),
      );
    });

    const uploadFile = async (
      localFileUri: string,
      type: 'image' | 'video',
      contentType: string,
      rotate?: number | undefined,
    ) => {
      fileUploader.enqueue({
        localFileUri,
        contentType,
        type,
        parameters: {
          cover: true,
          blurhash: true,
          videoRotate: rotate,
        },
        context: {
          type: 'extended-property',
          propertyId: property.id,
        },
      });
    };

    const retryUpload = usePersistFn((mediaInfo: GenericMediaInfo) => {
      if (readonly || mediaInfo.source?.type !== 'local') return;
      fileUploader.enqueue({
        localFileUri: mediaInfo.source.localFileUri,
        contentType: mediaInfo.source.contentType,
        type: mediaInfo.type,
        parameters: mediaInfo.source.uploadParameters,
        context: {
          type: 'extended-property',
          propertyId: property.id,
          mediaKey: mediaInfo.key,
        },
      });
    });

    const onAdd = usePersistFn(() => {
      navigation.navigate('_camera', {
        multiple: max > 1,
        maxMediaCountToSelect: max,
        caption: property.name,
        photoOnly: property.supportedFormats?.length
          ? !property.supportedFormats.includes('video')
          : undefined,
        videoOnly: property.supportedFormats?.length
          ? !property.supportedFormats.includes('image')
          : undefined,

        // handle media capture result
        onResult: result => {
          void uploadFile(
            result.uri,
            result.type === 'photo' ? 'image' : 'video',
            result.contentType ??
              mimeTypeFromFileUri(result.filename || result.file.path),
          );
          navigation.pop();
        },

        // handle asset selection from local gallery
        onMultipleAssetsSelected: async assets => {
          if (Platform.OS === 'web') {
            navigation.pop();
          } else {
            navigation.pop(2);
          }

          for (const asset of assets) {
            const type = asset.mediaType === 'photo' ? 'image' : 'video';
            if (Platform.OS === 'web') {
              void uploadFile(
                asset.uri,
                type,
                mimeTypeFromFileUri(
                  asset.filename ?? asset.uri,
                  type === 'image' ? 'image/jpeg' : 'video/mp4',
                ),
              );
            } else {
              const assetInfo =
                await MediaService.shared.getAssetInfoAsync(asset);
              if (assetInfo.localUri) {
                // https://www.adobe.com/hk_en/creativecloud/file-types/image/raster/heic-file.html
                if (/\.heics?$/i.test(assetInfo.localUri)) {
                  // convert the asset to jpeg format.
                  __DEV__ && console.log('converting heic to jpeg... ');
                  const res = await manipulateAsync(assetInfo.localUri, [], {
                    format: SaveFormat.JPEG,
                    compress: 0.85,
                  });
                  __DEV__ && console.log('converted image: ', res);
                  void uploadFile(res.uri, type, 'image/jpeg');
                } else {
                  void uploadFile(
                    assetInfo.localUri,
                    type,
                    mimeTypeFromFileUri(
                      assetInfo.localUri,
                      type === 'image' ? 'image/jpeg' : 'video/mp4',
                    ),
                  );
                }
              }
            }
          }
        },
      });
    });

    const setupFileUploaderEventListener = usePersistFn(() => {
      return fileUploader.addListener((e, task) => {
        if (
          task.request.context?.type !== 'extended-property' ||
          task.request.context.propertyId !== property.id
        ) {
          return;
        }

        interacted.current = true;

        const context = task.request.context;

        if (e.type === 'queued') {
          //#region file upload queued
          const updated = produce(medias$.value, draft => {
            if (context.mediaKey) {
              const index = draft.findIndex(x => x.key === context.mediaKey);
              if (index >= 0) {
                const media = draft[index];
                media.status = 'queued';
                media.source = {
                  type: 'local',
                  localFileUri: task.request.localFileUri,
                  contentType: task.request.contentType,
                  uploadParameters: task.request.parameters,
                  state: { status: 'queued' },
                };
                return;
              }
            }

            const id = nanoid();
            draft.push({
              id,
              key: task.key,
              url: task.request.localFileUri,
              type: task.request.type,
              status: 'queued',
              source: {
                type: 'local',
                localFileUri: task.request.localFileUri,
                contentType: task.request.contentType,
                uploadParameters: task.request.parameters,
                state: { status: 'queued' },
              },
            });
          });
          medias$.next(updated);
          //#endregion
        } else {
          const finishedUrlsBefore = medias$.value
            .map(x => (x.source?.type === 'remote' ? x.source.url : undefined))
            .filter(isNotNull)
            .join(',');

          const updated = produce(medias$.value, draft => {
            const media = draft.find(
              x => x.key === (context.mediaKey ?? task.key),
            );
            if (!media) {
              console.error(
                'cannot find staged extended property media object, this must be bug',
              );
              return;
            }
            if (media.source?.type !== 'local') return;
            switch (e.type) {
              case 'compressing': {
                media.source.state = {
                  status: 'compressing',
                  progress: e.progress,
                };
                media.status = 'compressing';
                return;
              }
              case 'begin': {
                media.source.state = { status: 'uploading', progress: 0 };
                media.status = 'uploading';
                return;
              }
              case 'progress': {
                media.source.state = {
                  status: 'uploading',
                  progress: e.progress,
                };
                if (media.status !== 'uploading') {
                  media.status = 'uploading';
                }
                return;
              }
              case 'error': {
                media.source.state = { status: 'error', error: e.error };
                media.status = 'error';
                return;
              }
              case 'abort': {
                if (task.status !== 'aborted') {
                  media.source.state = {
                    status: 'error',
                    error: e.error ?? new Error('File uploading has aborted'),
                  };
                  media.status = undefined;
                }
                return;
              }
              case 'finished': {
                const cover = e.result.extra?.cover;
                media.coverUrl =
                  cover != null && cover !== '' ? cover : undefined;
                media.source = {
                  type: 'remote',
                  url: e.result.url,
                  cid: e.result.name,
                };
                media.status = 'uploaded';
                return;
              }
              default:
                return;
            }
          });

          medias$.next(updated);

          const finishedUrlsAfter = updated
            .map(x => (x.source?.type === 'remote' ? x.source.url : undefined))
            .filter(isNotNull)
            .join(',');

          if (finishedUrlsAfter !== finishedUrlsBefore) {
            onMediasUpdated(updated);
          }
        }
      });
    });

    const onDelete = usePersistFn(async (media: GenericMediaInfo) => {
      if (
        !(await withConfirmation({
          title: '确认',
          message: `是否确认删除该${media.type === 'image' ? '图片' : '视频'}?`,
        }))
      ) {
        return;
      }

      interacted.current = true;

      const updated = produce(medias$.value, draft => {
        const index = draft.findIndex(x => x.key === media.key);
        if (index >= 0) {
          draft.splice(index, 1);
          fileUploader.abort(media.key);
        }
      });
      medias$.next(updated);

      onMediasUpdated(updated);
    });

    const onPreviewVideo = usePersistFn((media: GenericMediaInfo) => {
      navigation.navigate('_mediaPreview', { url: media.url, type: 'video' });
    });

    useEffect(() => {
      return setupFileUploaderEventListener();
    }, [setupFileUploaderEventListener]);

    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const sub = medias$.subscribe(medias => {
        if (interacted.current) {
          updateError(medias.length);
        }
      });
      return () => sub.unsubscribe();
    }, [medias$, updateError]);

    const numColumns = 4;
    const [cellSize] = computeGridLayoutItemSize({
      numberOfColumns: numColumns,
      aspectRatio: 1,
      cellMargin: rpx(5),
      gridPadding: rpx(10),
      gridWidth: width + rpx(10),
    });

    return (
      <>
        {helpText ? (
          <MaybeText
            text={helpText}
            css={`
              font-family: ${FontFamily.NotoSans.Light};
              font-size: ${rpx(12)}px;
              line-height: ${rpx(16)}px;
              color: ${theme.order.helpTextColor};
              margin-bottom: ${rpx(10)}px;
            `}
          />
        ) : null}
        <MediaListView
          medias={medias}
          editable={!readonly}
          canAdd={max > 0}
          width={width}
          numColumns={numColumns}
          cellSize={cellSize}
          cellMargin={rpx(5)}
          removeIconSize={rpx(16)}
          hideLabel={true}
          scrollEnabled={false}
          onAdd={onAdd}
          onDelete={onDelete}
          onRetryUpload={retryUpload}
          onPreviewVideo={onPreviewVideo}
          contentContainerStyle={{
            paddingTop: rpx(5),
          }}
        />
        {error ? (
          <ErrorText
            errorMsg={error}
            css={`
              margin-left: ${rpx(10)}px;
              margin-top: ${rpx(10)}px;
            `}
          />
        ) : null}
      </>
    );
  },
);
